/**
 * Canvas Save Queue
 * Manages debounced/throttled save operations for canvas changes
 * Figma-style: intelligent batching, priority handling, conflict resolution
 */

import type { CanvasUpdateInput } from '@/hooks/useCanvas'

export type SavePriority = 'HIGH' | 'MEDIUM' | 'LOW'

/**
 * A save payload. Page-level content (nodes, edges, page config) is bound to
 * the page it belongs to via `pageUuid` at enqueue time, so a flush that runs
 * after the user switched pages still writes to the correct page.
 */
export interface CanvasSavePayload extends Partial<CanvasUpdateInput> {
  pageUuid?: string
  /**
   * Revision the `nodes`/`edges` in this payload are based on. The server
   * refuses the write when the page has moved past it, so another tab's save
   * cannot be silently overwritten. Only content payloads carry it.
   */
  revision?: number
}

export interface SaveQueueItem {
  key: string
  data: CanvasSavePayload
  priority: SavePriority
  timestamp: number
  retries?: number
}

export interface SaveQueueConfig {
  immediate?: boolean
  debounce?: number
  throttle?: number
  priority?: SavePriority
}

const PRIORITY_ORDER: Record<SavePriority, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 }
const MAX_RETRIES = 3

function retryDelay(retryCount: number): number {
  return Math.min(1000 * Math.pow(2, retryCount - 1), 8000)
}

/**
 * Merge queue items: LOW priority first so HIGH overwrites, older first so newer overwrites
 */
function mergeItems(items: SaveQueueItem[]): CanvasSavePayload {
  const sorted = [...items].sort((a, b) =>
    PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority] || a.timestamp - b.timestamp
  )
  const merged: CanvasSavePayload = {}
  for (const item of sorted) {
    Object.assign(merged, item.data)
  }
  return merged
}

/**
 * Save queue manager
 * Handles debouncing, throttling, and priority-based save operations
 */
export class CanvasSaveQueue {
  private queue: Map<string, SaveQueueItem> = new Map()
  private timers: Map<string, NodeJS.Timeout> = new Map()
  private lastSaveTimes: Map<string, number> = new Map()
  private inFlight: Set<string> = new Set()
  private onSave: (changes: CanvasSavePayload) => Promise<void>
  private onQueueChange?: () => void

  /**
   * @param onSave Performs the save. Must reject on failure so the queue can retry.
   */
  constructor(
    onSave: (changes: CanvasSavePayload) => Promise<void>,
    onQueueChange?: () => void
  ) {
    this.onSave = onSave
    this.onQueueChange = onQueueChange
  }

  /**
   * Enqueue a save operation
   */
  enqueue(key: string, data: CanvasSavePayload, config: SaveQueueConfig = {}) {
    const {
      immediate = false,
      debounce = 0,
      throttle = 0,
      priority = 'MEDIUM'
    } = config

    // Clear existing timer for this key
    const existingTimer = this.timers.get(key)
    if (existingTimer) {
      clearTimeout(existingTimer)
      this.timers.delete(key)
    }

    // Add/update queue item
    const item: SaveQueueItem = {
      key,
      data,
      priority,
      timestamp: Date.now()
    }
    this.queue.set(key, item)
    this.onQueueChange?.() // Notify queue changed

    // Handle immediate save
    if (immediate) {
      this.flushKey(key)
      return
    }

    // Handle throttle
    if (throttle > 0) {
      const lastSaveTime = this.lastSaveTimes.get(key) || 0
      const timeSinceLastSave = Date.now() - lastSaveTime

      if (timeSinceLastSave < throttle) {
        // Schedule save after throttle period
        const delay = throttle - timeSinceLastSave
        const timer = setTimeout(() => {
          this.flushKey(key)
        }, delay)
        this.timers.set(key, timer)
        return
      }
    }

    // Handle debounce (default)
    const delay = debounce > 0 ? debounce : 1000 // Default 1s debounce
    const timer = setTimeout(() => {
      this.flushKey(key)
    }, delay)
    this.timers.set(key, timer)
  }

  /**
   * Flush a specific key (save immediately)
   * Skips if a save for this key is already in-flight to prevent concurrent saves
   */
  private async flushKey(key: string) {
    if (this.inFlight.has(key)) return // Skip if already saving this key

    const item = this.queue.get(key)
    if (!item) return

    // Remove from queue and timers
    this.queue.delete(key)
    const pendingTimer = this.timers.get(key)
    if (pendingTimer) clearTimeout(pendingTimer)
    this.timers.delete(key)
    this.onQueueChange?.() // Notify queue changed

    // Update last save time
    this.lastSaveTimes.set(key, Date.now())

    // Execute save with in-flight tracking
    this.inFlight.add(key)
    let retryScheduled = false
    try {
      await this.onSave(item.data)
    } catch (error) {
      console.error(`Failed to save ${key}:`, error)
      const retryCount = (item.retries || 0) + 1
      if (retryCount <= MAX_RETRIES) {
        // Newer data enqueued while in-flight supersedes the failed data field-by-field
        const newer = this.queue.get(key)
        const newerTimer = this.timers.get(key)
        if (newerTimer) clearTimeout(newerTimer)
        this.queue.set(key, {
          ...(newer ?? item),
          data: newer ? { ...item.data, ...newer.data } : item.data,
          retries: retryCount,
          timestamp: Date.now(),
        })
        // Schedule retry with exponential backoff
        const timer = setTimeout(() => {
          this.flushKey(key)
        }, retryDelay(retryCount))
        this.timers.set(key, timer)
        retryScheduled = true
        this.onQueueChange?.()
      } else {
        console.error(`Save permanently failed for ${key} after ${retryCount} attempts`)
      }
    } finally {
      this.inFlight.delete(key)
      // If new data was enqueued for this key while we were in-flight, flush it now
      // (a scheduled retry waits for its backoff instead)
      if (!retryScheduled && this.queue.has(key)) {
        this.flushKey(key)
      }
    }
  }

  /**
   * Flush all pending saves immediately.
   * Items are merged per target page (items without a page are canvas-level),
   * so content bound to different pages is never mixed into one request.
   */
  async flushAll() {
    // Clear all timers
    this.timers.forEach(timer => clearTimeout(timer))
    this.timers.clear()

    // Wait for any in-flight saves to settle (prevent concurrent saves to same endpoints)
    if (this.inFlight.size > 0) {
      await new Promise(resolve => setTimeout(resolve, 150))
    }

    const batches = this.getPendingBatches()
    if (batches.length === 0) return

    // Clear queue before save — but re-add on failure to prevent data loss
    this.queue.clear()
    this.onQueueChange?.()

    await Promise.all(batches.map(async (batch) => {
      try {
        await this.onSave(batch)
      } catch (error) {
        console.error('Failed to flush all saves:', error)
        // Re-enqueue merged data so it's not permanently lost, and retry with backoff
        const key = `flush-retry:${batch.pageUuid ?? 'canvas'}`
        const existing = this.queue.get(key)
        this.queue.set(key, {
          key,
          data: existing ? { ...batch, ...existing.data } : batch,
          priority: 'HIGH',
          timestamp: Date.now(),
          retries: 1,
        })
        const existingTimer = this.timers.get(key)
        if (existingTimer) clearTimeout(existingTimer)
        this.timers.set(key, setTimeout(() => {
          this.flushKey(key)
        }, retryDelay(1)))
        this.onQueueChange?.()
      }
    }))
  }

  /**
   * Clear all pending saves without executing
   */
  clear() {
    this.timers.forEach(timer => clearTimeout(timer))
    this.timers.clear()
    this.queue.clear()
    this.onQueueChange?.() // Notify queue changed
  }

  /**
   * Get current queue size
   */
  get size(): number {
    return this.queue.size
  }

  /**
   * Check if queue has pending saves
   */
  get hasPending(): boolean {
    return this.queue.size > 0
  }

  /**
   * Get merged pending data for emergency flush (e.g., beforeunload beacon)
   * Returns null if no pending data
   */
  getPendingData(): CanvasSavePayload | null {
    if (this.queue.size === 0) return null
    const merged = mergeItems(Array.from(this.queue.values()))
    return Object.keys(merged).length > 0 ? merged : null
  }

  /**
   * Get pending data merged per target page (canvas-level items grouped together).
   * Each batch carries its `pageUuid` (undefined for canvas-level data).
   */
  getPendingBatches(): CanvasSavePayload[] {
    const groups = new Map<string, SaveQueueItem[]>()
    for (const item of this.queue.values()) {
      const groupKey = item.data.pageUuid ?? ''
      const group = groups.get(groupKey)
      if (group) group.push(item)
      else groups.set(groupKey, [item])
    }
    const batches: CanvasSavePayload[] = []
    for (const items of groups.values()) {
      const merged = mergeItems(items)
      if (Object.keys(merged).length > 0) batches.push(merged)
    }
    return batches
  }
}
